import type { MediaType } from "@/interfaces/uploads";

/**
 * Single source of truth for upload constraints, keyed by R2 media slot. Enforced client-side
 * (UploadCard for images, CustomVideoUnlocked for the video's size) and mirrored server-side in
 * `apps/gfgf-api/src/constants/uploadLimits.ts` — keep the two in sync.
 *
 * Formats match the on-screen suggestions. The EMAIL logo is raster-only (PNG/JPG) — most email
 * clients (Gmail/Outlook) don't render SVG. The LANDING logo renders in a browser, so it keeps SVG.
 */
export interface UploadLimit {
  /** Accepted MIME types — also the file input's `accept` attribute. */
  readonly mimeTypes: readonly string[];
  readonly maxBytes: number;
  /** Human label for the size cap, e.g. "2MB". */
  readonly maxSizeLabel: string;
}

const MB = 1024 * 1024;

export const UPLOAD_LIMITS: Record<MediaType, UploadLimit> = {
  // Email logo: raster-only (SVG doesn't render in most email clients).
  logo: { mimeTypes: ["image/png", "image/jpeg"], maxBytes: 2 * MB, maxSizeLabel: "2MB" },
  // Landing logo: renders in a browser, so SVG is fine.
  "landing-logo": {
    mimeTypes: ["image/png", "image/jpeg", "image/svg+xml"],
    maxBytes: 2 * MB,
    maxSizeLabel: "2MB",
  },
  banner: { mimeTypes: ["image/png", "image/jpeg"], maxBytes: 5 * MB, maxSizeLabel: "5MB" },
  "landing-banner": {
    mimeTypes: ["image/png", "image/jpeg"],
    maxBytes: 5 * MB,
    maxSizeLabel: "5MB",
  },
  video: {
    mimeTypes: ["video/mp4", "video/webm"],
    maxBytes: 50 * MB,
    maxSizeLabel: "50MB",
  },
  // Not yet surfaced in the UI, but valid R2 slots — image defaults so the server never rejects
  // a future hero/message image for lack of a rule.
  hero: { mimeTypes: ["image/png", "image/jpeg"], maxBytes: 5 * MB, maxSizeLabel: "5MB" },
  message: { mimeTypes: ["image/png", "image/jpeg"], maxBytes: 5 * MB, maxSizeLabel: "5MB" },
};

/** `accept` attribute value for a media slot's file input. */
export function acceptFor(mediaType: MediaType): string {
  return UPLOAD_LIMITS[mediaType].mimeTypes.join(",");
}

const MIME_LABELS: Record<string, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/svg+xml": "SVG",
  "image/webp": "WebP",
  "video/mp4": "MP4",
  "video/webm": "WebM",
};

/**
 * Human upload hint for a slot, e.g. "PNG or JPG — max 2MB" — derived from the slot's own limits so
 * a shared component (email vs landing logo) always shows the right formats.
 */
export function formatsHint(mediaType: MediaType): string {
  const { mimeTypes, maxSizeLabel } = UPLOAD_LIMITS[mediaType];
  const labels = mimeTypes.map((m) => MIME_LABELS[m] ?? m);
  const list =
    labels.length <= 1
      ? (labels[0] ?? "")
      : `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
  return `${list} — max ${maxSizeLabel}`;
}
